// Coastal rocks (pure, deterministic, no RNG state): sea stacks standing out of the shallows and boulders at the
// foot of cliffs. Beaches and cliff faces are the shore ramp / land shader now (render/shore/); this file only
// places the rocks, from the same shore data, so it is geometry-free and unit-testable without WebGL.
//
// Everything derives from the terrain through hexNoise: it never flickers, is identical on every client, and
// survives save/load (the sim stores only biomes). Capped, instanced (CoastalDressing.tsx), and never on land,
// so it cannot overlap settlements or units standing on tiles.

import { TILE_RADIUS, hexNoise, waterSurfaceY } from './elevation.js';
import type { ShoreBoard } from './shore/shoreBoard.js';
import { DIST_MAX } from './shore/shoreField.js';

export { CLIFF_MIN_RISE, coastKindFor } from './shore/shoreKind.js';
export type { CoastKind } from './shore/shoreKind.js';

export type RockKind = 'spire' | 'mesa' | 'twin' | 'boulder';

export interface RockPiece {
  readonly kind: RockKind;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly rotY: number;
  readonly sx: number;
  readonly sy: number;
  readonly sz: number;
  /** 0..1 per-piece tone, mapped to a colour by the renderer. */
  readonly tone: number;
  /** Radius of the foam ring at the rock's foot (0 = none). */
  readonly foam: number;
  /** Y of the water surface around the rock (the foam ring lies on it). */
  readonly surface: number;
  /** "q,r" of the tile the rock stands in (fog of war). */
  readonly tile: string;
}

/** Hard cap on rocks (instances) for a whole board, whatever its size: memory stays flat. */
export const MAX_ROCKS = 260;
/** Chance a sea tile touching land carries rocks / the chance for a tile one ring further out. */
export const ROCK_SHARE_NEAR = 0.65;
export const ROCK_SHARE_FAR = 0.3;
const MIN_SPACING = 0.5;
const APOTHEM = TILE_RADIUS * Math.cos(Math.PI / 6);

function pickKind(h: number): RockKind {
  if (h < 0.36) return 'spire';
  if (h < 0.56) return 'mesa';
  if (h < 0.76) return 'twin';
  return 'boulder';
}

/** Size [radius, height] by kind and a 0..1 roll. */
function sizeFor(kind: RockKind, a: number, b: number): [number, number] {
  switch (kind) {
    case 'spire':
      return [0.17 + a * 0.1, 0.42 + b * 0.4];
    case 'mesa':
      return [0.28 + a * 0.14, 0.24 + b * 0.18];
    case 'twin':
      return [0.25 + a * 0.1, 0.4 + b * 0.34];
    default:
      return [0.22 + a * 0.13, 0.22 + b * 0.18];
  }
}

/**
 * Rocks for a board: sea stacks scattered through the shallows (some right at the shore, some out in the
 * blue), each with a foam ring, plus a couple of boulders at the foot of every cliff stretch. Pure: the same
 * board gives the same rocks, in a stable order, never more than MAX_ROCKS.
 */
export function buildRocks(board: ShoreBoard): RockPiece[] {
  const out: RockPiece[] = [];
  if (board.field.empty) return out;
  const { biomeMap, field, edges } = board;
  const taken: Array<[number, number]> = [];
  const free = (x: number, z: number) =>
    taken.every(([tx, tz]) => Math.hypot(tx - x, tz - z) >= MIN_SPACING);

  // Boulders at the foot of cliffs: one or two per rocky edge, just outside the waterline.
  for (const e of edges) {
    if (e.kind !== 'cliff') continue;
    if (out.length >= MAX_ROCKS) break;
    const h = hexNoise(e.q * 3.3 + e.i * 1.9, e.r * 2.1 - e.i * 0.7);
    if (h > 0.62) continue;
    const n = h < 0.25 ? 2 : 1;
    const mx = (e.ax + e.bx) / 2;
    const mz = (e.az + e.bz) / 2;
    for (let s = 0; s < n; s++) {
      const hs = hexNoise(e.q * 5.1 + e.i * 2.3 + s * 7.7, e.r * 4.7 - s * 3.1);
      const out0 = Math.max(0.05, e.top - 0.03 - e.waterY) + 0.1 + hs * 0.12;
      const along = (s - (n - 1) / 2) * 0.5 + (hs - 0.5) * 0.2;
      const x = mx + e.nx * out0 - e.nz * along;
      const z = mz + e.nz * out0 + e.nx * along;
      if (!free(x, z)) continue;
      const [rad, hgt] = sizeFor('boulder', hs, 1 - hs);
      taken.push([x, z]);
      out.push({
        kind: 'boulder',
        x,
        y: e.waterY - 0.08,
        z,
        rotY: hs * Math.PI * 2,
        sx: rad,
        sy: hgt,
        sz: rad * (0.8 + hs * 0.4),
        tone: hs,
        foam: rad * 1.55,
        surface: e.waterY,
        tile: `${e.q},${e.r}`,
      });
    }
  }

  // Sea stacks: in sea tiles within ~two tiles of land, away from the tile edges (so the shore ramp, the foam
  // line and the neighbouring water keep a clear read).
  const seaTiles = [...biomeMap.entries()]
    .filter(([, b]) => b === 'ocean' || b === 'coast')
    .map(([k]) => k.split(',').map(Number) as [number, number])
    .sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  for (const [q, r] of seaTiles) {
    if (out.length >= MAX_ROCKS) break;
    const cx = Math.sqrt(3) * (q + r / 2);
    const cz = 1.5 * r;
    const d0 = field.distAt(cx, cz);
    if (d0 > 2.1 || d0 >= DIST_MAX) continue;
    const share = d0 < 1.3 ? ROCK_SHARE_NEAR : ROCK_SHARE_FAR;
    if (hexNoise(q * 1.13 + 5.5, r * 1.91 - 2.5) >= share) continue;
    const waterY = waterSurfaceY(q, r, biomeMap);
    const count = 1 + Math.floor(hexNoise(q * 4.1, r * 3.7) * (d0 < 1.3 ? 2.2 : 1.99));
    for (let s = 0; s < count && out.length < MAX_ROCKS; s++) {
      // Up to a few tries to find a clear spot in the shallows (distance 0.35..1.9 from land).
      for (let t = 0; t < 5; t++) {
        const ang = hexNoise(q * 6.1 + s * 2.2 + t * 3.7, r * 5.3 + 1 + t) * Math.PI * 2;
        const rad =
          Math.sqrt(hexNoise(q * 2.7 - s + t * 1.3, r * 7.9 + s * 1.3 - t)) * (APOTHEM - 0.22);
        const x = cx + Math.cos(ang) * rad;
        const z = cz + Math.sin(ang) * rad;
        const d = field.distAt(x, z);
        if (d < 0.35 || d > 1.9 || !free(x, z)) continue;
        const k = hexNoise(q * 3.1 + s * 5.9 + t, r * 2.9 - s * 4.3);
        const kind = pickKind(k);
        const a = hexNoise(q * 8.3 + s, r * 1.7 + t * 2);
        const b = hexNoise(q * 1.9 - t, r * 9.1 + s * 3);
        const [rd, hgt] = sizeFor(kind, a, b);
        taken.push([x, z]);
        out.push({
          kind,
          x,
          y: waterY - 0.12,
          z,
          rotY: a * Math.PI * 2,
          sx: rd,
          sy: hgt,
          sz: rd * (0.85 + b * 0.35),
          tone: k,
          foam: rd * 1.6,
          surface: waterY,
          tile: `${q},${r}`,
        });
        break;
      }
    }
  }
  return out;
}

/**
 * Which rocks show under fog of war: visible tiles, or explored ones (a dimmed map still has its rocks);
 * unexplored tiles show none. One flag per rock, in `rocks` order. With no visibility set everything shows.
 */
export function rockVisibility(
  rocks: ReadonlyArray<RockPiece>,
  visibleCoords?: ReadonlySet<string> | null,
  exploredCoords?: ReadonlySet<string>,
): Uint8Array {
  const flags = new Uint8Array(rocks.length);
  for (let i = 0; i < rocks.length; i++) {
    const t = rocks[i]!.tile;
    flags[i] = !visibleCoords || visibleCoords.has(t) || exploredCoords?.has(t) ? 1 : 0;
  }
  return flags;
}
